import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import {
  Box,
  Drawer,
  Hidden,
  IconButton,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import useAuthStore from 'clientStore/useAuthStore';
import useBranchStore from 'clientStore/useBranchStore';
import useThemeStore from 'clientStore/useThemeStore';
import Logo from 'components/logo';
import NavSection from 'components/nav-section';
import MyTooltip from 'components/MyTooltip';
import useResponsive from 'hooks/useResponsive';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { extractMeUser } from 'utils/companyHelpers';
import useNavConfig from './useNavConfig';

const NAV_WIDTH = 250;
const NAV_WIDTH_CLOSE = 80;

export default function Nav({ openNav, onCloseNav, toggleNav }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const navConfig = useNavConfig();
  const queryClient = useQueryClient();
  const { setAccessToken } = useAuthStore();
  const { clearSelectedBranchId } = useBranchStore();
  const theme = useTheme();
  const { data } = queryClient.getQueryState(['me']) || {};
  const meUser = extractMeUser(data);
  const isDesktop = useResponsive('up', 'lg');
  const { setTheme, mode } = useThemeStore();

  useEffect(() => {
    if (openNav && !isDesktop) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const username = meUser?.username || '';
  const role = meUser?.role;

  const logout = () => {
    setAccessToken(null);
    clearSelectedBranchId();
    navigate('/', { replace: true });
  };

  const toggleTheme = () => {
    setTheme(mode === 'dark' ? 'light' : 'dark');
  };

  const ProfileBlock = openNav ? (
    <Box
      sx={{
        width: '100%',
        px: 1.5,
        py: 1.5,
        borderTop: '1px solid',
        borderColor: 'divider',
        flexShrink: 0
      }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <AccountBoxIcon sx={{ width: 32, height: 32, color: 'text.secondary', flexShrink: 0 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" fontWeight={600} noWrap color="text.primary">
            {username || '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {role === '' || role == null ? 'SUPERADMIN' : role}
          </Typography>
        </Box>
        <MyTooltip title="Logout" placement="top">
          <IconButton size="small" onClick={logout} aria-label="logout" sx={{ flexShrink: 0 }}>
            <LogoutIcon fontSize="small" />
          </IconButton>
        </MyTooltip>
      </Stack>
    </Box>
  ) : (
    <Box
      sx={{
        width: '100%',
        py: 1.5,
        borderTop: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        flexShrink: 0
      }}>
      <IconButton size="small" onClick={toggleTheme} aria-label="toggle-theme">
        {mode === 'dark' ? (
          <Brightness7Icon sx={{ color: 'primary.main' }} fontSize="small" />
        ) : (
          <Brightness4Icon fontSize="small" />
        )}
      </IconButton>
      <IconButton size="small" onClick={logout} aria-label="logout">
        <LogoutIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  const renderContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
      <Hidden lgDown>
        <Box
          sx={{
            px: openNav ? 2 : 1,
            py: 2,
            display: 'flex',
            flexDirection: openNav ? 'row' : 'column',
            alignItems: 'center',
            justifyContent: openNav ? 'space-between' : 'center',
            gap: 1,
            width: '100%',
            flexShrink: 0
          }}>
          <Logo openNav={openNav} />
          <IconButton
            onClick={toggleNav}
            size="small"
            aria-label={openNav ? 'sidebar-close' : 'sidebar-open'}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              flexShrink: 0
            }}>
            {openNav ? (
              <KeyboardArrowLeftIcon fontSize="small" />
            ) : (
              <KeyboardArrowRightIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
      </Hidden>

      <Hidden lgUp>
        <Box sx={{ px: 2, py: 2, width: '100%', flexShrink: 0 }}>
          <Logo />
        </Box>
      </Hidden>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          overflowY: 'auto',
          overflowX: 'hidden'
        }}>
        <NavSection data={navConfig} openNav={openNav || !isDesktop} />
      </Box>

      {openNav && isDesktop && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.75, flexShrink: 0 }}>
          <IconButton size="small" onClick={toggleTheme} aria-label="toggle-theme">
            {mode === 'dark' ? (
              <Brightness7Icon sx={{ color: 'primary.main' }} fontSize="small" />
            ) : (
              <Brightness4Icon fontSize="small" />
            )}
          </IconButton>
        </Box>
      )}

      {ProfileBlock}
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: openNav ? NAV_WIDTH : NAV_WIDTH_CLOSE },
        transition: (t) =>
          t.transitions.create('width', {
            easing: t.transitions.easing.sharp,
            duration: t.transitions.duration.enteringScreen
          })
      }}>
      {isDesktop ? (
        <Drawer
          open
          variant="permanent"
          PaperProps={{
            sx: {
              bgcolor: 'background.default',
              borderRightStyle: 'dashed',
              width: openNav ? NAV_WIDTH : NAV_WIDTH_CLOSE,
              overflowX: 'hidden',
              height: '100%',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen
              })
            }
          }}>
          {renderContent}
        </Drawer>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{
            keepMounted: true
          }}
          PaperProps={{
            sx: { width: NAV_WIDTH }
          }}>
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
